import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";

import type { PasswordReset, UserAttributes } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import PasswordInput from "~/components/login/passwordInput/PasswordInput";
import FormBaseErrors from "~/components/ui/formBaseErrors/FormBaseErrors";
import Modal from "~/components/ui/modal/Modal";
import { useAuth } from "~/contexts/AuthProvider";
import labels from "~/helpers/labels";
import { tryCatch, tryCatchParsed } from "~/helpers/tryCatch";
import { useFormErrors } from "~/hooks/useFormErrors";

/**
 * Non-dismissable password setup for provisioned accounts (e.g. demo
 * platforms): the user signed in via a one-time email link and never knew
 * their generated password. While `user.force_password_change` is true the
 * backend accepts a password update without `current_password`; setting one
 * clears the flag, and the refetched user closes the modal.
 */
const ForcePasswordChangeModal = () => {
  const { user, refetchUser } = useAuth();

  // `PasswordReset` only to satisfy PasswordInput's register typing —
  // `reset_password_token` is never rendered or sent.
  const {
    register,
    handleSubmit,
    // `isSubmitting` (not the mutation's `isPending`) drives the busy state:
    // it also spans the `refetchUser()` await below, so the form cannot be
    // resubmitted in the window between the password update resolving and
    // the refetched user unmounting the modal — by then the backend has
    // already cleared the flag and would reject a second no-current-password
    // update.
    formState: { errors, isSubmitting },
    getValues,
    setError,
  } = useForm<PasswordReset>();
  const { baseErrors, applyServerErrors } = useFormErrors(setError);

  const { mutateAsync } = useMutation({
    mutationFn: (data: Partial<UserAttributes>) =>
      apiClient.user.userPartialUpdate(data as UserAttributes),
  });

  if (!user?.force_password_change) return null;

  const onSubmit = async (data: PasswordReset) => {
    // The outer `tryCatch` absorbs what `tryCatchParsed` rethrows (network
    // failure, non-JSON error body): without it the rejection would escape
    // the handler unhandled and the user would get no feedback at all.
    const result = await tryCatch(
      tryCatchParsed(
        mutateAsync({
          password: data.password,
          password_confirmation: data.password_confirmation,
        }),
      ),
    );

    if (result.error) {
      applyServerErrors({
        code: 0,
        message: "Something went wrong. Please try again.",
      });

      return;
    }

    if (result.data.error) {
      applyServerErrors(result.data.error);

      return;
    }

    // The flag is cleared server-side by the password update; the refetched
    // user unmounts the modal.
    await refetchUser();
  };

  return (
    <Modal
      isOpen
      disableDismiss
      hideCancel
      closeModal={() => {}}
      modalLabel={<h3>Set your password</h3>}
      buttonLabel="Save password"
      action={() => handleSubmit(onSubmit)()}
      actionButtonColor="primary"
      isLoading={isSubmitting}
      size="sm"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(onSubmit)();
        }}
      >
        <p>
          Welcome! To keep your account secure, please choose a password
          you&apos;ll use to sign in from now on.
        </p>
        <FormBaseErrors errors={baseErrors} />
        <PasswordInput
          passwordError={errors.password?.message}
          register={register}
          label={labels.NEW_PASSWORD}
          field="password"
          getValues={getValues}
          disabled={isSubmitting}
        />
        <PasswordInput
          passwordError={errors.password_confirmation?.message}
          register={register}
          label={labels.CONFIRM_PASSWORD}
          field="password_confirmation"
          getValues={getValues}
          disabled={isSubmitting}
        />
      </form>
    </Modal>
  );
};

export default ForcePasswordChangeModal;
